<template>
  <div>
    <!-- Backdrop -->
    <div
      v-if="visible"
      class="admin-drawer-backdrop"
      @click="$emit('close')"
    ></div>

    <!-- Drawer panel -->
    <transition name="drawer-slide">
      <div v-if="visible" class="admin-drawer">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center p-3 drawer-header">
          <div class="d-flex align-items-center">
            <div
              class="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold mr-3"
              :class="statusBgClass"
              style="width:40px;height:40px;font-size:.8rem;flex-shrink:0;"
            >
              {{ initials }}
            </div>
            <div>
              <h6 class="mb-0 font-weight-bold" style="color:#0f172a;">{{ company ? company.name : '' }}</h6>
              <span :class="['pill mr-1', statusBadgeClass]">{{ statusLabel }}</span>
              <span :class="['pill', healthBadge.cls]" :title="'Health score: ' + healthScore">{{ healthBadge.label }}</span>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <!-- Actions dropdown -->
            <div class="dropdown mr-2">
              <button class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" data-flip="false">
                Actions
              </button>
              <div class="dropdown-menu dropdown-menu-right" style="min-width:220px;border:1px solid #e9ecef;border-radius:12px;">

                <!-- View -->
                <h6 class="dropdown-header" style="font-size:.7rem;">View</h6>
                <a v-if="can('dukakit.impersonate-company')" class="dropdown-item" @click="$emit('impersonate', company)">
                  <i class="bi bi-person-badge mr-2"></i> View as Owner
                </a>
                <a class="dropdown-item" @click="copyEmail">
                  <i class="bi bi-clipboard mr-2"></i> Copy Owner Email
                </a>

                <!-- Billing -->
                <div v-if="can('dukakit.manage-billing') || can('dukakit.extend-trial') || can('dukakit.waive-billing')" class="dropdown-divider"></div>
                <h6 v-if="can('dukakit.manage-billing') || can('dukakit.extend-trial') || can('dukakit.waive-billing')" class="dropdown-header" style="font-size:.7rem;">Billing</h6>
                <a v-if="can('dukakit.manage-billing')" class="dropdown-item" @click="$emit('billing-date', company)">
                  <i class="bi bi-calendar-event mr-2"></i> Adjust Billing Date
                </a>
                <a v-if="can('dukakit.manage-billing')" class="dropdown-item" @click="$emit('change-plan', company)">
                  <i class="bi bi-arrow-repeat mr-2"></i> Change Plan
                </a>
                <a v-if="can('dukakit.extend-trial')" class="dropdown-item" @click="$emit('extend-trial', company)">
                  <i class="bi bi-hourglass-split mr-2"></i> Extend Trial
                </a>
                <a v-if="can('dukakit.waive-billing')" class="dropdown-item" @click="$emit('waive-overdue', company)">
                  <i class="bi bi-check-circle mr-2"></i> Waive Overdue
                </a>
                <a v-if="can('dukakit.extend-trial')" class="dropdown-item" @click="$emit('convert-trial', company)">
                  <i class="bi bi-lightning mr-2"></i> Convert Trial → Active
                </a>

                <!-- Account -->
                <div class="dropdown-divider"></div>
                <h6 class="dropdown-header" style="font-size:.7rem;">Account</h6>
                <a v-if="can('dukakit.manage-affiliates')" class="dropdown-item" @click="$emit('assign-affiliate', company)">
                  <i class="bi bi-people mr-2"></i> Assign Affiliate
                </a>
                <a class="dropdown-item" @click="$emit('mark-test', company)">
                  <i class="bi bi-tag mr-2"></i> {{ company && company.is_test ? 'Unmark Test' : 'Mark as Test' }}
                </a>

                <!-- Comms -->
                <div v-if="can('dukakit.send-notifications')" class="dropdown-divider"></div>
                <h6 v-if="can('dukakit.send-notifications')" class="dropdown-header" style="font-size:.7rem;">Comms</h6>
                <a v-if="can('dukakit.send-notifications')" class="dropdown-item" @click="$emit('notify', company)">
                  <i class="bi bi-bell mr-2"></i> Send Notification
                </a>

                <!-- Security -->
                <div v-if="can('dukakit.manage-sessions') || can('dukakit.impersonate-company')" class="dropdown-divider"></div>
                <h6 v-if="can('dukakit.manage-sessions') || can('dukakit.impersonate-company')" class="dropdown-header" style="font-size:.7rem;">Security</h6>
                <a v-if="can('dukakit.manage-sessions')" class="dropdown-item" @click="$emit('force-logout', company)">
                  <i class="bi bi-shield-x mr-2"></i> Force Logout All Sessions
                </a>
                <a v-if="can('dukakit.impersonate-company')" class="dropdown-item" @click="$emit('password-reset', company)">
                  <i class="bi bi-key mr-2"></i> Send Password Reset
                </a>

                <!-- Danger -->
                <div v-if="can('dukakit.suspend-company') || can('dukakit.archive-company') || can('dukakit.delete-company') || can('dukakit.delete-inventory')" class="dropdown-divider"></div>
                <h6 v-if="can('dukakit.suspend-company') || can('dukakit.archive-company') || can('dukakit.delete-company') || can('dukakit.delete-inventory')" class="dropdown-header text-danger" style="font-size:.7rem;">Danger</h6>
                <a v-if="can('dukakit.suspend-company')" class="dropdown-item text-danger" @click="$emit('reactivate', company)">
                  <i class="bi bi-arrow-counterclockwise mr-2"></i> Reactivate
                </a>
                <a v-if="can('dukakit.suspend-company')" class="dropdown-item text-danger" @click="$emit('suspend', company)">
                  <i class="bi bi-slash-circle mr-2"></i> Suspend
                </a>
                <a v-if="can('dukakit.archive-company')" class="dropdown-item text-danger" @click="$emit('archive', company)">
                  <i class="bi bi-archive mr-2"></i> Archive
                </a>
                <a v-if="can('dukakit.delete-inventory')" class="dropdown-item text-danger" @click="$emit('delete-inventory', company)">
                  <i class="bi bi-trash mr-2"></i> Delete Inventory
                </a>
                <a v-if="can('dukakit.delete-company')" class="dropdown-item text-danger font-weight-bold" @click="$emit('delete-company', company)">
                  <i class="bi bi-x-octagon mr-2"></i> Delete Company
                </a>
              </div>
            </div>
            <button class="btn btn-light" @click="$emit('close')">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <div v-else-if="detail" class="admin-drawer-body">
          <!-- Tabs -->
          <div class="d-flex px-3 pt-2 drawer-tabs">
            <a
              v-for="tab in tabs"
              :key="tab.key"
              class="drawer-tab"
              :class="{ active: activeTab === tab.key }"
              @click="activeTab = tab.key"
            >
              {{ tab.label }}
            </a>
          </div>

          <div class="p-3">
            <!-- Overview tab -->
            <div v-if="activeTab === 'overview'">
              <table class="table table-borderless">
                <tbody>
                  <tr>
                    <th class="text-secondary pl-0" style="width:40%;">Industry</th>
                    <td>{{ detail.industry || '—' }}</td>
                  </tr>
                  <tr>
                    <th class="text-secondary pl-0">Phone</th>
                    <td>{{ detail.phone || '—' }}</td>
                  </tr>
                  <tr>
                    <th class="text-secondary pl-0">Email</th>
                    <td>{{ detail.email || '—' }}</td>
                  </tr>
                  <tr>
                    <th class="text-secondary pl-0">Country</th>
                    <td>{{ detail.companysettings ? detail.companysettings.country : '—' }}</td>
                  </tr>
                  <tr>
                    <th class="text-secondary pl-0">Created</th>
                    <td>{{ formatDate(detail.created_at) }}</td>
                  </tr>
                  <tr>
                    <th class="text-secondary pl-0">Last Update</th>
                    <td>{{ formatDate(detail.updated_at) }}</td>
                  </tr>
                  <tr v-if="detail.user_activity_summary">
                    <th class="text-secondary pl-0">Last Activity</th>
                    <td class="text-primary">{{ fromNow(detail.user_activity_summary.updated_at) }}</td>
                  </tr>
                  <tr v-if="detail.user_activity_summary && detail.user_activity_summary.logactivity">
                    <th class="text-secondary pl-0">Last Route</th>
                    <td><code style="font-size:.75rem;">{{ detail.user_activity_summary.logactivity.route }}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Billing tab -->
            <div v-if="activeTab === 'billing'">
              <div v-if="detail.billing">
                <table class="table table-borderless">
                  <tbody>
                    <tr v-if="detail.billing.subscription_plan">
                      <th class="text-secondary pl-0" style="width:40%;">Plan</th>
                      <td>{{ detail.billing.subscription_plan.name }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Amount</th>
                      <td>
                        <strong>KES {{ Number(detail.billing.effective_amount).toLocaleString() }}</strong>
                        <span v-if="detail.billing.is_custom" class="pill pill-secondary ml-1">Custom</span>
                      </td>
                    </tr>
                    <tr v-if="detail.billing.billing_notes">
                      <th class="text-secondary pl-0">Notes</th>
                      <td><small class="text-secondary">{{ detail.billing.billing_notes }}</small></td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Due Date</th>
                      <td :class="isDuePast ? 'text-danger font-weight-bold' : ''">
                        {{ formatDate(detail.billing.due_date) }}
                        <span v-if="isDuePast" class="pill pill-danger ml-1">Overdue</span>
                      </td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Status</th>
                      <td>{{ detail.billing.status || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-secondary text-center py-3">
                <small>No billing record</small>
              </div>
            </div>

            <!-- Owner tab -->
            <div v-if="activeTab === 'owner'">
              <div v-if="detail.owner">
                <table class="table table-borderless">
                  <tbody>
                    <tr>
                      <th class="text-secondary pl-0" style="width:40%;">Name</th>
                      <td>{{ detail.owner.name }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Email</th>
                      <td>{{ detail.owner.email }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Phone</th>
                      <td>{{ detail.owner.phone || '—' }}</td>
                    </tr>
                  </tbody>
                </table>

                <!-- Branches -->
                <div v-if="detail.owner.companybranches && detail.owner.companybranches.length > 0">
                  <h6 class="font-weight-bold mt-3 mb-2">Branches</h6>
                  <div
                    v-for="branch in detail.owner.companybranches"
                    :key="branch.id"
                    class="d-flex justify-content-between py-1 border-bottom"
                    v-if="branch.company"
                  >
                    <small>{{ branch.company.name }}</small>
                    <small class="text-secondary">
                      {{ branch.company.billing && branch.company.billing.subscription_plan
                          ? branch.company.billing.subscription_plan.name
                          : '—' }}
                    </small>
                  </div>
                </div>
              </div>
              <div v-else class="text-secondary text-center py-3">
                <small>Owner details unavailable</small>
              </div>
            </div>

            <!-- Settings tab -->
            <div v-if="activeTab === 'settings'">
              <div v-if="detail.companysettings">
                <table class="table table-borderless">
                  <tbody>
                    <tr>
                      <th class="text-secondary pl-0" style="width:55%;">Enable Discounts</th>
                      <td>{{ detail.companysettings.enable_discounts ? 'Yes' : 'No' }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Enable Taxes</th>
                      <td>{{ detail.companysettings.enable_taxes ? 'Yes' : 'No' }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Min Stock Alert</th>
                      <td>{{ detail.companysettings.minimums_stock || '—' }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Currency</th>
                      <td>{{ detail.companysettings.currency || '—' }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">Reservations</th>
                      <td>{{ detail.companysettings.has_reservations ? 'Enabled' : 'Disabled' }}</td>
                    </tr>
                    <tr>
                      <th class="text-secondary pl-0">KDS</th>
                      <td>{{ detail.companysettings.has_kds ? 'Enabled' : 'Disabled' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-secondary text-center py-3">
                <small>No settings data</small>
              </div>
            </div>

            <!-- Notes tab -->
            <div v-if="activeTab === 'notes' && can('dukakit.manage-notes')">
              <div v-if="notesLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
              </div>
              <div v-else>
                <div class="mb-3">
                  <textarea
                    v-model="newNote"
                    class="form-control"
                    rows="3"
                    placeholder="Add an internal note… (visible only to Dukakit staff)"
                  ></textarea>
                  <button
                    class="btn btn-primary mt-2"
                    :disabled="savingNote || !newNote.trim()"
                    @click="saveNote"
                  >
                    <span v-if="savingNote" class="spinner-border spinner-border-sm mr-1"></span>
                    Save Note
                  </button>
                </div>
                <div v-if="notes.length === 0" class="text-center text-secondary py-2">
                  <small>No notes yet</small>
                </div>
                <div
                  v-for="note in notes"
                  :key="note.id"
                  class="border rounded p-2 mb-2"
                  style="font-size:.82rem;background:#f8fafc;"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <span class="font-weight-bold text-primary">{{ note.author }}</span>
                      <span class="text-secondary ml-2" style="font-size:.75rem;">{{ fromNow(note.created_at) }}</span>
                    </div>
                    <button class="btn btn-link p-0 text-danger" @click="deleteNote(note.id)">
                      <i class="bi bi-trash" style="font-size:.75rem;"></i>
                    </button>
                  </div>
                  <p class="mb-0 mt-1" style="white-space:pre-wrap;">{{ note.note }}</p>
                </div>
              </div>
            </div>

            <!-- Timeline tab -->
            <div v-if="activeTab === 'timeline'">
              <div v-if="timelineLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
              </div>
              <div v-else-if="timeline.length === 0" class="text-center text-secondary py-3">
                <small>No activity recorded yet</small>
              </div>
              <div v-else>
                <div
                  v-for="(event, i) in timeline"
                  :key="i"
                  class="d-flex mb-3"
                >
                  <div
                    class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 mr-2 text-white"
                    :class="timelineColor(event.color)"
                    style="width:28px;height:28px;font-size:.65rem;"
                  >
                    <i :class="['bi', event.icon]"></i>
                  </div>
                  <div class="flex-grow-1 border-bottom pb-2" style="min-width:0;">
                    <div class="d-flex justify-content-between align-items-start">
                      <span class="font-weight-bold" style="font-size:.82rem;">{{ event.title }}</span>
                      <small class="text-muted flex-shrink-0 ml-1" style="font-size:.7rem;">{{ fromNow(event.timestamp) }}</small>
                    </div>
                    <small class="text-muted" style="font-size:.75rem;">{{ event.description }}</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Feedback tab -->
            <div v-if="activeTab === 'feedback' && can('dukakit.manage-feedback')">
              <form @submit.prevent="submitFeedback" class="mt-2">
                <div class="form-group">
                  <label class="font-weight-bold">Customer Name</label>
                  <input v-model="feedbackForm.feedbackName" class="form-control" />
                </div>
                <div class="form-row">
                  <div class="form-group col-6">
                    <label class="font-weight-bold">Email</label>
                    <input v-model="feedbackForm.feedbackEmail" class="form-control" type="email" />
                  </div>
                  <div class="form-group col-6">
                    <label class="font-weight-bold">Phone</label>
                    <input v-model="feedbackForm.feedbackPhone" class="form-control" />
                  </div>
                </div>
                <div class="form-group">
                  <label class="font-weight-bold">Feedback</label>
                  <textarea v-model="feedbackForm.feedback" class="form-control" rows="3"></textarea>
                </div>
                <div class="form-group">
                  <label class="font-weight-bold">Type</label>
                  <div>
                    <div class="form-check form-check-inline" v-for="t in ['complement','complain','feature']" :key="t">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'fb-' + t"
                        :value="t"
                        v-model="feedbackForm.feedbackType"
                      />
                      <label class="form-check-label text-capitalize" :for="'fb-' + t">{{ t }}</label>
                    </div>
                  </div>
                </div>
                <button class="btn btn-primary" type="submit" :disabled="submittingFeedback">
                  <span v-if="submittingFeedback" class="spinner-border spinner-border-sm mr-1"></span>
                  Submit Feedback
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { dukakitCan } from '../../utils/dukakitCan';

export default {
  name: 'AdminCompanyDrawer',

  props: {
    company: { type: Object, default: null },
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      detail: null,
      activeTab: 'overview',
      feedbackForm: {
        feedbackType: [],
        feedback: '',
        feedbackName: '',
        feedbackPhone: '',
        feedbackEmail: '',
      },
      submittingFeedback: false,
      notes: [],
      notesLoading: false,
      newNote: '',
      savingNote: false,
      timeline: [],
      timelineLoading: false,
    };
  },

  computed: {
    ...mapGetters(['userDetails']),

    tabs() {
      const base = [
        { key: 'overview', label: 'Overview' },
        { key: 'billing', label: 'Billing' },
        { key: 'owner', label: 'Owner' },
        { key: 'settings', label: 'Settings' },
        { key: 'timeline', label: 'Timeline' },
      ];
      if (this.can('dukakit.manage-feedback')) {
        base.push({ key: 'feedback', label: 'Feedback' });
      }
      if (this.can('dukakit.manage-notes')) {
        base.push({ key: 'notes', label: 'Notes' });
      }
      return base;
    },

    initials() {
      const name = this.company && this.company.name ? this.company.name : '?';
      const parts = name.trim().split(' ');
      return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0][0].toUpperCase();
    },

    companyStatus() {
      if (!this.company) return 'inactive';
      const days = moment().diff(moment(this.company.created_at), 'days');
      if (days <= 30) return 'trial';
      if (this.company.billing && moment(this.company.billing.due_date).isAfter(moment())) return 'active';
      return 'inactive';
    },

    statusLabel() {
      return { active: 'Active', trial: 'Trial', inactive: 'Inactive' }[this.companyStatus];
    },

    statusBgClass() {
      return { active: 'bg-primary', trial: 'bg-dark', inactive: 'bg-secondary' }[this.companyStatus];
    },

    statusBadgeClass() {
      return { active: 'pill-primary', trial: 'pill-dark', inactive: 'pill-secondary' }[this.companyStatus];
    },

    isDuePast() {
      return this.detail && this.detail.billing && moment(this.detail.billing.due_date).isBefore(moment());
    },

    healthScore() {
      if (!this.company) return 0;
      let score = 100;
      if (!this.company.billing) {
        score -= 20;
      } else if (moment(this.company.billing.due_date).isBefore(moment())) {
        const days = moment().diff(moment(this.company.billing.due_date), 'days');
        score -= Math.min(45, days * 3);
      }
      if (!this.company.user_activity_summary) {
        score -= 35;
      } else {
        const days = moment().diff(moment(this.company.user_activity_summary.updated_at), 'days');
        if (days > 90) score -= 40;
        else if (days > 30) score -= 20;
        else if (days > 14) score -= 8;
      }
      return Math.max(0, Math.min(100, score));
    },

    healthBadge() {
      const s = this.healthScore;
      if (s >= 80) return { label: 'Healthy', cls: 'pill-primary' };
      if (s >= 60) return { label: 'Good',    cls: 'pill-dark' };
      if (s >= 40) return { label: 'Fair',    cls: 'pill-secondary' };
      return              { label: 'At Risk', cls: 'pill-danger'  };
    },
  },

  watch: {
    company(val) {
      if (val && this.visible) {
        this.loadDetail(val.id);
      }
    },
    visible(val) {
      if (val && this.company) {
        this.loadDetail(this.company.id);
        this.activeTab = 'overview';
      } else if (!val) {
        this.detail   = null;
        this.notes    = [];
        this.timeline = [];
      }
    },

    activeTab(val) {
      if (val === 'notes' && this.notes.length === 0) {
        this.fetchNotes();
      }
      if (val === 'timeline' && this.timeline.length === 0) {
        this.fetchTimeline();
      }
    },
  },

  methods: {
    can(permission) {
      return dukakitCan(this.userDetails, permission);
    },

    async loadDetail(companyId) {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/company-full-details', {
          params: { company_id: companyId },
        });
        this.detail = data;
      } catch (e) {
        // silent
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      return moment(date).format('D MMM YYYY, h:mm A');
    },

    fromNow(date) {
      return moment(date).fromNow();
    },

    async submitFeedback() {
      this.submittingFeedback = true;
      try {
        await axios.post('/api/customer-feedback', {
          ...this.feedbackForm,
          company_id: this.company.id,
        });
        this.feedbackForm = { feedbackType: [], feedback: '', feedbackName: '', feedbackPhone: '', feedbackEmail: '' };
        this.$toasts.push({ message: 'Feedback saved', type: 'success' });
      } finally {
        this.submittingFeedback = false;
      }
    },

    async fetchNotes() {
      if (!this.company) return;
      this.notesLoading = true;
      try {
        const { data } = await axios.get(`/api/admin/companies/${this.company.id}/notes`);
        this.notes = data;
      } finally {
        this.notesLoading = false;
      }
    },

    async saveNote() {
      if (!this.newNote.trim()) return;
      this.savingNote = true;
      try {
        const { data } = await axios.post(`/api/admin/companies/${this.company.id}/notes`, {
          note: this.newNote.trim(),
        });
        this.notes   = data.notes;
        this.newNote = '';
      } catch (e) {
        this.$toasts.push({ message: 'Failed to save note', type: 'error' });
      } finally {
        this.savingNote = false;
      }
    },

    async deleteNote(noteId) {
      try {
        const { data } = await axios.delete(`/api/admin/companies/${this.company.id}/notes/${noteId}`);
        this.notes = data.notes;
      } catch (e) {
        this.$toasts.push({ message: 'Failed to delete note', type: 'error' });
      }
    },

    copyEmail() {
      const email = (this.detail && this.detail.email) || (this.company && this.company.email) || '';
      if (!email) return;
      navigator.clipboard.writeText(email).then(() => {
        this.$toasts.push({ message: `Copied: ${email}`, type: 'success' });
      });
    },

    async fetchTimeline() {
      if (!this.company) return;
      this.timelineLoading = true;
      try {
        const { data } = await axios.get(`/api/admin/companies/${this.company.id}/timeline`);
        this.timeline = data;
      } finally {
        this.timelineLoading = false;
      }
    },

    timelineColor(color) {
      return {
        success: 'bg-primary',
        primary: 'bg-primary',
        warning: 'bg-dark',
        danger:  'bg-danger',
        info:    'bg-dark',
      }[color] || 'bg-secondary';
    },
  },
};
</script>

<style scoped>
/* Sits below Bootstrap's modal-backdrop (1040) / modal (1050) on purpose,
   so any modal (Adjust Billing Date, Change Plan, msgBoxConfirm, etc.)
   opened from a drawer action always renders above the drawer instead of
   tying with it on z-index — on mobile the drawer is ~full width, so a
   tie made it look like the modal never opened until the drawer closed. */
.admin-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1032;
}

.admin-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 95vw;
  height: 100vh;
  background: #fff;
  z-index: 1035;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
}

.drawer-header {
  border-bottom: 1px solid #e9ecef;
}

.drawer-tabs {
  gap: 4px;
  border-bottom: 1px solid #e9ecef;
  background: #fff;
}
.drawer-tab {
  padding: 8px 12px;
  font-size: .82rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.drawer-tab:hover {
  color: #0f172a;
}
.drawer-tab.active {
  color: #0f172a;
  font-weight: 600;
  border-bottom-color: #0f172a;
}

.pill {
  display: inline-block;
  font-size: .68rem;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #0f172a;
}
.pill-primary { background: rgba(13,110,253,.1); color: #0d6efd; }
.pill-dark { background: #f1f5f9; color: #0f172a; }
.pill-secondary { background: #f1f5f9; color: #64748b; }
.pill-danger { background: rgba(220,53,69,.1); color: #dc3545; }

.admin-drawer-body {
  flex: 1;
  overflow-y: auto;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.25s ease;
}
.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
